import React from "react";
import { adminApi } from "../lib/api";
import { Gift, Trophy, RefreshCw } from "lucide-react";

export default function ReferralPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await adminApi().get("/admin/referrals");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="text-brand-text2">Indlæser referral-data...</div>;
  if (error) return <div className="rounded-xl border border-red-300 bg-red-50 p-3 text-red-800">{error}</div>;
  if (!data) return null;

  const totals = data.totals || {};
  return (
    <div className="space-y-5" data-testid="referral-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-brand-dark flex items-center gap-2">
            <Gift size={20} className="text-brand-red" />
            Henvisningsprogram
          </h2>
          <p className="text-sm text-brand-text2 mt-1">
            Eksisterende kunder anbefaler EMA Kurer → begge får 50 kr rabat
          </p>
        </div>
        <button
          onClick={load}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 inline-flex items-center gap-1.5"
          data-testid="referral-refresh"
        >
          <RefreshCw size={14} /> Opdater
        </button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="Anbefalingskoder" value={totals.total_codes} />
        <StatCard label="Henvisninger i alt" value={totals.total_referrals} />
        <StatCard label="Afventer" value={totals.pending} tone="amber" />
        <StatCard label="Fuldført" value={totals.completed} tone="green" />
        <StatCard label="Udbetalt (kr)" value={(totals.total_rewards_dkk ?? 0).toFixed(0)} tone="red" />
      </div>

      {/* Top referrers */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="font-display text-lg font-bold text-brand-dark mb-3 flex items-center gap-2">
          <Trophy size={18} className="text-amber-500" />
          Top 10 ambassadører
        </h3>
        {data.top_referrers.length === 0 ? (
          <p className="text-sm text-brand-text2" data-testid="referral-top-empty">Ingen henvisninger endnu — del programmet på sociale medier og via faktura-emails.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="referral-top-table">
              <thead className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">Inviteret</th>
                  <th className="py-2 pr-3">Afventer</th>
                  <th className="py-2 pr-3">Fuldført</th>
                  <th className="py-2 pr-3">Optjent (kr)</th>
                </tr>
              </thead>
              <tbody>
                {data.top_referrers.map((r, i) => (
                  <tr key={r.email} className="border-b border-slate-100 last:border-b-0" data-testid={`referral-top-row-${i}`}>
                    <td className="py-2 pr-3 font-bold text-slate-400">#{i + 1}</td>
                    <td className="py-2 pr-3 font-semibold text-brand-dark">{r.email}</td>
                    <td className="py-2 pr-3">{r.invited}</td>
                    <td className="py-2 pr-3">
                      <span className="inline-block rounded-md bg-amber-100 text-amber-800 px-2 py-0.5 text-xs">{r.pending}</span>
                    </td>
                    <td className="py-2 pr-3">
                      <span className="inline-block rounded-md bg-green-100 text-green-800 px-2 py-0.5 text-xs font-bold">{r.completed}</span>
                    </td>
                    <td className="py-2 pr-3 font-bold text-brand-red">{r.earned_dkk?.toFixed(0)} kr</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent referrals */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="font-display text-lg font-bold text-brand-dark mb-3">Seneste henvisninger</h3>
        {data.recent.length === 0 ? (
          <p className="text-sm text-brand-text2">Ingen endnu.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="referral-recent-table">
              <thead className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="py-2 pr-3">Dato</th>
                  <th className="py-2 pr-3">Referrer</th>
                  <th className="py-2 pr-3">Referee</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Reward-kode</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="py-2 pr-3 text-xs text-brand-text2">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-2 pr-3 font-semibold text-brand-dark">{r.referrer_email}</td>
                    <td className="py-2 pr-3 text-brand-text2">{r.referee_email}</td>
                    <td className="py-2 pr-3">
                      {r.status === "completed" ? (
                        <span className="inline-block rounded-md bg-green-100 text-green-800 px-2 py-0.5 text-xs">Fuldført</span>
                      ) : (
                        <span className="inline-block rounded-md bg-amber-100 text-amber-800 px-2 py-0.5 text-xs">Afventer</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{r.reward_code_for_referrer || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-white",
    green: "border-green-200 bg-green-50",
    amber: "border-amber-200 bg-amber-50",
    red: "border-red-200 bg-red-50",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`}>
      <div className="text-xs uppercase text-brand-text2">{label}</div>
      <div className="mt-2 text-2xl font-bold text-brand-dark">{value ?? 0}</div>
    </div>
  );
}
